import { cn } from "@/lib/utils"
import {
  forwardRef,
  useRef,
  type DragEventHandler,
  type SyntheticEvent,
} from "react"

interface RichTextAreaProps {
  value: string
  onChange: (next: string) => void
  readOnly?: boolean
  rows?: number
  className?: string
  onDrop?: DragEventHandler<HTMLTextAreaElement>
  onDragOver?: DragEventHandler<HTMLTextAreaElement>
}

// Shared text metrics so the transparent textarea and the highlight backdrop
// line up exactly. Keep these identical on both layers.
const TEXT_CLS =
  "whitespace-pre-wrap wrap-break-word font-sans text-[13px] leading-[1.72] p-[14px]"

function renderHighlighted(value: string) {
  return value.split(/(\{\{\w+\}\})/).map((part, i) =>
    /^\{\{\w+\}\}$/.test(part) ? (
      <span
        key={i}
        data-var-pill="true"
        className="rounded-[3px] bg-yellow1/15 text-yellow1"
      >
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  )
}

export const RichTextArea = forwardRef<HTMLTextAreaElement, RichTextAreaProps>(
  function RichTextArea(
    { value, onChange, readOnly, rows = 9, className, onDrop, onDragOver },
    forwardedRef,
  ) {
    const backdropRef = useRef<HTMLDivElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)

    function setRefs(node: HTMLTextAreaElement | null) {
      textareaRef.current = node
      if (typeof forwardedRef === "function") forwardedRef(node)
      else if (forwardedRef) forwardedRef.current = node
    }

    return (
      <div
        className={cn(
          "relative overflow-hidden rounded-lg border border-grey4 bg-grey2",
          !readOnly && "focus-within:border-yellow1",
          className,
        )}
      >
        {/* highlight backdrop */}
        <div
          ref={backdropRef}
          aria-hidden
          className={cn(TEXT_CLS, "pointer-events-none text-[#e6e6e6]")}
          style={{ minHeight: `${rows * 1.72}em` }}
        >
          {renderHighlighted(value)}
          {/* trailing newline keeps backdrop height in sync with a trailing blank line */}
          {value.endsWith("\n") ? "​" : null}
        </div>
        {/* transparent editable layer */}
        {!readOnly && (
          <textarea
            ref={setRefs}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onScroll={(e) => {
              if (backdropRef.current)
                backdropRef.current.scrollTop = e.currentTarget.scrollTop
            }}
            onDrop={onDrop}
            onDragOver={onDragOver}
            spellCheck={false}
            className={cn(
              TEXT_CLS,
              "absolute inset-0 h-full w-full resize-none border-0 bg-transparent text-transparent caret-white outline-hidden",
            )}
          />
        )}
      </div>
    )
  },
)

interface RichTextInputProps {
  id?: string
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  /** The box: border, background, height. */
  className?: string
  /** Font size and padding, shared by both layers so they line up. */
  textClassName?: string
  onDrop?: DragEventHandler<HTMLInputElement>
  onDragOver?: DragEventHandler<HTMLInputElement>
}

/**
 * The one-line sibling of RichTextArea, for a subject: {{variables}} in the
 * same yellow as in the message. A transparent input over a highlighted copy
 * of its value; the copy follows the input's horizontal scroll, so the two
 * stay in line on text longer than the box.
 */
export const RichTextInput = forwardRef<HTMLInputElement, RichTextInputProps>(
  function RichTextInput(
    {
      id,
      value,
      onChange,
      disabled,
      className,
      textClassName = "px-3 py-[7px] text-sm leading-5",
      onDrop,
      onDragOver,
    },
    forwardedRef,
  ) {
    const backdropRef = useRef<HTMLDivElement>(null)
    // Inputs scroll without a reliable event: re-align on whatever moves it.
    const follow = (event: SyntheticEvent<HTMLInputElement>) => {
      if (backdropRef.current)
        backdropRef.current.scrollLeft = event.currentTarget.scrollLeft
    }

    return (
      <div className={cn("relative overflow-hidden", className)}>
        <div
          ref={backdropRef}
          aria-hidden
          className={cn(
            "pointer-events-none overflow-hidden font-sans whitespace-pre text-foreground",
            textClassName,
            disabled && "opacity-50",
          )}
        >
          {renderHighlighted(value)}
          {/* Keeps the line's height while the value is empty. */}
          {"\u200b"}
        </div>
        <input
          id={id}
          ref={forwardedRef}
          value={value}
          disabled={disabled}
          spellCheck={false}
          onChange={(event) => {
            onChange(event.target.value)
            follow(event)
          }}
          onScroll={follow}
          onSelect={follow}
          onKeyUp={follow}
          onDrop={onDrop}
          onDragOver={onDragOver}
          className={cn(
            "absolute inset-0 h-full w-full border-0 bg-transparent font-sans text-transparent caret-foreground outline-hidden disabled:cursor-not-allowed",
            textClassName,
          )}
        />
      </div>
    )
  },
)
